/**
 * Test kit registration: intercepts ATC on 'test-kits' tagged products, modal flow,
 * validation, line item properties, cart refresh.
 *
 * Rebuy `.rebuy-button` clicks are blocked by `test-kit-rebuy-guard.js`, which
 * requests the form through the `test-kit:register` event or the
 * `test_kit_register` URL parameter.
 */
(function () {
  "use strict";

  const cfg = window.testKitRegistrationConfig;
  if (!cfg || !cfg.enabled) {
    return;
  }

  const SEL_FORM = "[data-test-kit-registration-form]";
  const DIALOG_ID = "test-kit-registration-dialog";

  const TITLES = ["", "Mr", "Mrs", "Miss", "Ms", "Mx", "Dr"];
  const GENDERS = ["", "Female", "Male", "M>F Female", " F>M Male"];

  const DUP_EMAIL_MSG = "A unique email address is required";
  const EMAIL_INVALID_MSG = "Please enter a valid email address";

  // Product fields rendered on the page, restored after a Rebuy add.
  const pageProduct = {
    productId: cfg.productId,
    defaultVariantId: cfg.defaultVariantId,
    testKitAgeRule: cfg.testKitAgeRule,
    productTitle: cfg.productTitle,
  };

  /**
   * @returns {string}
   */
  function getAgeMessageUnder18() {
    const title =
      (cfg.productTitle && String(cfg.productTitle).trim()) || "this";
    return (
      "Anyone under 18, we recommend arranging Iron or B12 testing through " +
      `your GP. Our ${title} finger-prick test is still available ` +
      "for under 18s to use."
    );
  }

  /**
   * A kit added from a Rebuy widget is not always the product rendered on the
   * page, so its fields are applied for the length of the registration.
   *
   * @param {object} product config fields of the kit being registered
   */
  function setActiveProduct(product) {
    cfg.productId = product.productId;
    cfg.defaultVariantId = product.defaultVariantId;
    cfg.testKitAgeRule = product.testKitAgeRule;
    cfg.productTitle = product.productTitle;
  }

  /** @type {ReturnType<typeof createEmptyPerson>[]} */
  let persons = [];
  let editingLineKey = null;
  /** @type {string | null} */
  let lastFocused = null;
  /** @type {HTMLElement[]} */
  let trapFocusables = [];

  /**
   * @returns {object}
   */
  function createEmptyPerson() {
    return {
      title: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      gender: "",
      dob: "",
      same_as_delivery_address: true,
      address_1: "",
      address_2: "",
      town: "",
      state: "",
      postcode: "",
      collapsed: false,
    };
  }

  function getDialog() {
    return document.getElementById(DIALOG_ID);
  }

  /**
   * @returns {boolean} false while a kit added from a Rebuy widget is active
   */
  function isPageProductActive() {
    return String(cfg.productId) === String(pageProduct.productId);
  }

  function getVariantId() {
    // The page form belongs to the page product, never to an upsold kit.
    const form = isPageProductActive()
      ? document.querySelector(SEL_FORM)
      : null;
    const input = form ? form.querySelector("[data-variant-id]") : null;
    if (input && input.value) {
      return parseInt(input.value, 10);
    }
    return cfg.defaultVariantId;
  }

  function syncVariantFromForm() {
    const v = getVariantId();
    if (v) {
      cfg.defaultVariantId = v;
    }
  }

  /**
   * Auto-formats DOB digits into DD/MM/YYYY as the user types. A trailing "/"
   * is appended after the day and month segments while typing forward, but not
   * while deleting (so backspace can pass through the separators).
   *
   * @param {string} value current raw field value
   * @param {boolean} isDelete true when the input was a deletion
   * @returns {string}
   */
  function formatDobInput(value, isDelete) {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    if (digits.length > 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }
    if (digits.length === 4) {
      const base = `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
      return isDelete ? base : `${base}/`;
    }
    if (digits.length > 2) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    if (digits.length === 2) {
      return isDelete ? digits : `${digits}/`;
    }
    return digits;
  }

  /**
   * @param {string} dobDisplay
   * @returns {string} ISO date YYYY-MM-DD or ""
   */
  function parseDobUk(dobDisplay) {
    const t = dobDisplay.trim();
    const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (!m) {
      return "";
    }
    let d = parseInt(m[1], 10);
    let mo = parseInt(m[2], 10);
    let y = parseInt(m[3], 10);
    if (y < 100) {
      y += y >= 30 ? 1900 : 2000;
    }
    if (mo < 1 || mo > 12 || d < 1 || d > 31) {
      return "";
    }
    const dt = new Date(Date.UTC(y, mo - 1, d));
    if (
      dt.getUTCFullYear() !== y ||
      dt.getUTCMonth() !== mo - 1 ||
      dt.getUTCDate() !== d
    ) {
      return "";
    }
    const mm = String(mo).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  }

  /**
   * @param {string} iso
   * @returns {number}
   */
  function ageFromIso(iso) {
    if (!iso) {
      return NaN;
    }
    const born = new Date(iso + "T12:00:00Z");
    const today = new Date();
    let age = today.getUTCFullYear() - born.getUTCFullYear();
    const m = today.getUTCMonth() - born.getUTCMonth();
    if (m < 0 || (m === 0 && today.getUTCDate() < born.getUTCDate())) {
      age--;
    }
    return age;
  }

  /**
   * @param {object} p
   * @returns {object}
   */
  function buildPersonPayload(p) {
    const fn = (p.first_name || "").trim();
    const ln = (p.last_name || "").trim();
    const full = [fn, ln].filter(Boolean).join(" ");
    const iso = parseDobUk(p.dob || "");
    const out = {
      title: (p.title || "").trim(),
      first_name: fn,
      last_name: ln,
      full_name: full,
      email: (p.email || "").trim(),
      phone: (p.phone || "").trim(),
      gender: (p.gender || "").trim(),
      dob: iso,
      same_as_delivery_address: !!p.same_as_delivery_address,
    };
    if (!out.same_as_delivery_address) {
      out.address_1 = (p.address_1 || "").trim();
      out.address_2 = (p.address_2 || "").trim();
      out.town = (p.town || "").trim();
      out.state = (p.state || "").trim();
      out.postcode = (p.postcode || "").trim();
    }
    return out;
  }

  /**
   * @param {object} person
   * @returns {boolean} true when DOB parses to a valid under-18 age
   */
  function isPersonUnder18(person) {
    const iso = parseDobUk((person && person.dob) || "");
    if (!iso) {
      return false;
    }
    const age = ageFromIso(iso);
    return !Number.isNaN(age) && age < 18;
  }

  /**
   * Under-18s are only blocked for products whose age rule restricts minors
   * (Iron / B12). Products configured with any other rule (e.g. "none" for the
   * Vitamin D kit) allow under-18 registrations.
   *
   * @param {object} person
   * @returns {boolean}
   */
  function isPersonAgeBlocked(person) {
    const rule = String(cfg.testKitAgeRule || "none")
      .trim()
      .toLowerCase();
    if (rule !== "iron" && rule !== "b12") {
      return false;
    }
    return isPersonUnder18(person);
  }

  function validateEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  /**
   * Validates one person and returns a map of field -> error message for the
   * fields that fail. Empty object means the person is valid.
   *
   * @param {object} p
   * @returns {Record<string, string>}
   */
  function validatePersonFields(p) {
    const fe = {};
    const pl = buildPersonPayload(p);
    if (!pl.first_name) {
      fe.first_name = "First name is required.";
    }
    if (!pl.last_name) {
      fe.last_name = "Last name is required.";
    }
    if (!pl.email) {
      fe.email = "Email is required.";
    } else if (!validateEmail(pl.email)) {
      fe.email = EMAIL_INVALID_MSG;
    }
    if (!pl.phone) {
      fe.phone = "Phone number is required.";
    }
    if (!pl.gender) {
      fe.gender = "Gender is required.";
    }
    if (!p.dob || !pl.dob) {
      fe.dob = "Date of birth is required (DD/MM/YYYY).";
    }
    if (!pl.same_as_delivery_address) {
      if (!pl.address_1) {
        fe.address_1 = "Address line 1 is required.";
      }
      if (!pl.town) {
        fe.town = "Town is required.";
      }
      if (!pl.state) {
        fe.state = "State is required.";
      }
      if (!pl.postcode) {
        fe.postcode = "Postcode is required.";
      }
    }
    return fe;
  }

  /**
   * Clears all inline field errors and error borders.
   */
  function clearAllFieldErrors() {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    dialog.querySelectorAll("[data-field-error]").forEach((el) => {
      el.textContent = "";
      el.removeAttribute("role");
    });
    dialog
      .querySelectorAll(".test-kit-registration__control--error")
      .forEach((el) => {
        el.classList.remove("test-kit-registration__control--error");
      });
  }

  /**
   * Clears the inline error and error border for a single field.
   *
   * @param {number} idx
   * @param {string} field
   */
  function clearFieldError(idx, field) {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    const errEl = dialog.querySelector(
      `[data-field-error="${field}"][data-person="${idx}"]`,
    );
    if (errEl) {
      errEl.textContent = "";
      errEl.removeAttribute("role");
    }
    const control = dialog.querySelector(
      `[data-field="${field}"][data-person="${idx}"]`,
    );
    if (control) {
      control.classList.remove("test-kit-registration__control--error");
    }
  }

  /**
   * Shows the inline error and error border for a single field.
   *
   * @param {number} idx
   * @param {string} field
   * @param {string} msg
   */
  function setFieldError(idx, field, msg) {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    const errEl = dialog.querySelector(
      `[data-field-error="${field}"][data-person="${idx}"]`,
    );
    if (errEl) {
      errEl.textContent = msg;
      errEl.setAttribute("role", "alert");
    }
    const control = dialog.querySelector(
      `[data-field="${field}"][data-person="${idx}"]`,
    );
    if (control) {
      control.classList.add("test-kit-registration__control--error");
    }
  }

  /**
   * Renders per-field errors below each input and flags borders.
   *
   * @param {Record<string, string>[]} errorsByPerson
   * @returns {Element | null} the first invalid control, for focus/scroll
   */
  function showFieldErrors(errorsByPerson) {
    const dialog = getDialog();
    if (!dialog) {
      return null;
    }
    clearAllFieldErrors();
    let firstInvalid = null;
    errorsByPerson.forEach((fields, idx) => {
      Object.keys(fields).forEach((field) => {
        const errEl = dialog.querySelector(
          `[data-field-error="${field}"][data-person="${idx}"]`,
        );
        if (errEl) {
          errEl.textContent = fields[field];
          errEl.setAttribute("role", "alert");
        }
        const control = dialog.querySelector(
          `[data-field="${field}"][data-person="${idx}"]`,
        );
        if (control) {
          control.classList.add("test-kit-registration__control--error");
          if (!firstInvalid) {
            firstInvalid = control;
          }
        }
      });
    });
    return firstInvalid;
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /**
   * @param {unknown} raw
   * @returns {Record<string, unknown>}
   */
  function normalizeLineItemProperties(raw) {
    if (typeof window.__betterYouNormalizeCartLineProperties === "function") {
      return window.__betterYouNormalizeCartLineProperties(raw);
    }
    if (raw == null) {
      return {};
    }
    if (Array.isArray(raw)) {
      const out = {};
      raw.forEach((entry) => {
        if (!entry || typeof entry !== "object" || entry.name == null) {
          return;
        }
        out[String(entry.name)] =
          entry.value != null && entry.value !== "" ? String(entry.value) : "";
      });
      return out;
    }
    if (typeof raw === "object") {
      return /** @type {Record<string, unknown>} */ (raw);
    }
    return {};
  }

  const TK_FIELD_NAMES = [
    "title",
    "first_name",
    "last_name",
    "full_name",
    "email",
    "phone",
    "gender",
    "dob",
    "same_as_delivery_address",
    "address_1",
    "address_2",
    "town",
    "state",
    "postcode",
  ];

  /**
   * Unique id per cart line so Shopify keeps separate lines (same variant +
   * batch /cart/add.js can otherwise merge or mis-assign properties).
   *
   * @returns {string}
   */
  function newTestKitLineUid() {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return `tk-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * One cart line = one person. Keys: `_test_kit_email`, `_test_kit_first_name`, …
   *
   * @param {object} p
   * @returns {Record<string, string>}
   */
  function buildFlatPropertiesForOnePerson(p) {
    const pre = "_test_kit_";
    const props = {};
    props[`${pre}title`] = p.title != null ? String(p.title) : "";
    props[`${pre}first_name`] =
      p.first_name != null ? String(p.first_name) : "";
    props[`${pre}last_name`] = p.last_name != null ? String(p.last_name) : "";
    props[`${pre}full_name`] = p.full_name != null ? String(p.full_name) : "";
    props[`${pre}email`] = p.email != null ? String(p.email) : "";
    props[`${pre}phone`] = p.phone != null ? String(p.phone) : "";
    props[`${pre}gender`] = p.gender != null ? String(p.gender) : "";
    props[`${pre}dob`] = p.dob != null ? String(p.dob) : "";
    props[`${pre}same_as_delivery_address`] = p.same_as_delivery_address
      ? "true"
      : "false";
    props[`${pre}address_1`] = p.address_1 != null ? String(p.address_1) : "";
    props[`${pre}address_2`] = p.address_2 != null ? String(p.address_2) : "";
    props[`${pre}town`] = p.town != null ? String(p.town) : "";
    props[`${pre}state`] = p.state != null ? String(p.state) : "";
    props[`${pre}postcode`] = p.postcode != null ? String(p.postcode) : "";
    props._test_kit_line_uid = newTestKitLineUid();
    return props;
  }

  /**
   * Reads one line's `_test_kit_*` props, legacy `_test_kit_{n}_*`, or JSON blob.
   *
   * @param {Record<string, unknown>} properties
   * @returns {object[]}
   */
  function extractPersonsFromLineItemProperties(properties) {
    const props = normalizeLineItemProperties(properties);
    if (!props || typeof props !== "object") {
      return [];
    }
    const email = props._test_kit_email ?? props["_test_kit_email"];
    if (email != null && String(email).trim() !== "") {
      const p = {};
      TK_FIELD_NAMES.forEach((f) => {
        const k = `_test_kit_${f}`;
        const v = props[k];
        if (f === "same_as_delivery_address") {
          p[f] = v === true || v === "true";
        } else {
          p[f] = v != null ? String(v) : "";
        }
      });
      return [p];
    }
    const numbered = [];
    for (let i = 1; i <= 50; i++) {
      const pre = `_test_kit_${i}_`;
      const em = props[`${pre}email`];
      if (em == null || String(em).trim() === "") {
        break;
      }
      const row = {};
      TK_FIELD_NAMES.forEach((f) => {
        const v = props[`${pre}${f}`];
        if (f === "same_as_delivery_address") {
          row[f] = v === true || v === "true";
        } else {
          row[f] = v != null ? String(v) : "";
        }
      });
      numbered.push(row);
    }
    if (numbered.length) {
      return numbered;
    }
    const raw = props._test_kit_persons;
    if (!raw || typeof raw !== "string") {
      return [];
    }
    try {
      const data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  function updateEditModeUi() {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    const editing = Boolean(editingLineKey);
    const addBtn = dialog.querySelector("[data-test-kit-add-person]");
    if (addBtn) {
      addBtn.hidden = editing;
    }
    const submitBtn = dialog.querySelector("[data-test-kit-submit]");
    if (submitBtn) {
      submitBtn.textContent = editing ? "Update" : "Add to Cart";
    }
  }

  function renderPersonFields() {
    const root = getDialog();
    if (!root) {
      return;
    }
    const host = root.querySelector("[data-test-kit-persons-host]");
    if (!host) {
      return;
    }
    host.innerHTML = "";

    persons.forEach((person, idx) => {
      const card = document.createElement("div");
      card.className = "test-kit-registration__person-card";
      card.setAttribute("data-person-index", String(idx));

      // Only label persons when there's more than one; a lone person needs no
      // "Person 1" heading.
      if (persons.length > 1) {
        const header = document.createElement("div");
        header.className = "test-kit-registration__person-header";
        const ht = document.createElement("p");
        ht.className = "test-kit-registration__person-title";
        ht.textContent = `Person ${idx + 1}`;
        header.appendChild(ht);

        if (!person.collapsed) {
          const actions = document.createElement("div");
          actions.className = "test-kit-registration__person-actions";
          const removeBtn = document.createElement("button");
          removeBtn.type = "button";
          removeBtn.className = "test-kit-registration__text-btn";
          removeBtn.textContent = "Remove";
          removeBtn.setAttribute("data-remove-person", String(idx));
          actions.appendChild(removeBtn);
          header.appendChild(actions);
        }

        card.appendChild(header);
      }

      if (person.collapsed) {
        appendPersonSummary(card, person, idx);
        host.appendChild(card);
        return;
      }

      card.appendChild(
        buildSection(
          "Full Name",
          `
          <div class="test-kit-registration__stack">
            <div class="test-kit-registration__field">
              <label class="test-kit-registration__sr" for="tk-title-${idx}">Title</label>
              <select id="tk-title-${idx}" class="test-kit-registration__select" data-field="title" data-person="${idx}" aria-label="Title">
                <option value="" disabled${!person.title ? " selected" : ""}>Title</option>
                ${TITLES.filter(Boolean)
                  .map((t) => {
                    const sel = t === person.title ? " selected" : "";
                    return `<option value="${escapeHtml(t)}"${sel}>${escapeHtml(t)}</option>`;
                  })
                  .join("")}
              </select>
            </div>
            <div class="test-kit-registration__row">
              <div class="test-kit-registration__field">
                <label class="test-kit-registration__sr" for="tk-fn-${idx}">First name</label>
                <input id="tk-fn-${idx}" class="test-kit-registration__input" type="text" autocomplete="given-name" placeholder="First Name" data-field="first_name" data-person="${idx}" value="${escapeHtml(person.first_name || "")}">
                <p class="test-kit-registration__field-error" data-field-error="first_name" data-person="${idx}"></p>
              </div>
              <div class="test-kit-registration__field">
                <label class="test-kit-registration__sr" for="tk-ln-${idx}">Last name</label>
                <input id="tk-ln-${idx}" class="test-kit-registration__input" type="text" autocomplete="family-name" placeholder="Last Name" data-field="last_name" data-person="${idx}" value="${escapeHtml(person.last_name || "")}">
                <p class="test-kit-registration__field-error" data-field-error="last_name" data-person="${idx}"></p>
              </div>
            </div>
          </div>`,
        ),
      );

      const dobDisplay =
        person.dob && /^\d{4}-\d{2}-\d{2}$/.test(person.dob)
          ? `${person.dob.slice(8, 10)}/${person.dob.slice(5, 7)}/${person.dob.slice(0, 4)}`
          : person.dob || "";
      const dobBlockedInit = isPersonAgeBlocked(person);

      card.appendChild(
        buildSection(
          "Your Details*",
          `
          <div class="test-kit-registration__stack">
            <div class="test-kit-registration__field">
              <label class="test-kit-registration__sr" for="tk-email-${idx}">Email</label>
              <input id="tk-email-${idx}" class="test-kit-registration__input" type="email" autocomplete="email" placeholder="Email" data-field="email" data-person="${idx}" value="${escapeHtml(person.email || "")}">
              <p class="test-kit-registration__field-error" data-field-error="email" data-person="${idx}"></p>
            </div>
            <div class="test-kit-registration__field">
              <label class="test-kit-registration__sr" for="tk-phone-${idx}">Contact number</label>
              <input id="tk-phone-${idx}" class="test-kit-registration__input" type="tel" autocomplete="tel" placeholder="Contact Number" data-field="phone" data-person="${idx}" value="${escapeHtml(person.phone || "")}">
              <p class="test-kit-registration__field-error" data-field-error="phone" data-person="${idx}"></p>
            </div>
            <div class="test-kit-registration__field">
              <label class="test-kit-registration__sr" for="tk-dob-${idx}">Date of birth</label>
              <div class="test-kit-registration__input-wrap">
                <input id="tk-dob-${idx}" class="test-kit-registration__input" type="text" inputmode="numeric" placeholder="DD/MM/YYYY" data-field="dob" data-person="${idx}" value="${escapeHtml(dobDisplay)}" aria-describedby="tk-dob-hint-${idx}">
                <span class="test-kit-registration__input-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="4" width="14" height="13" rx="1" stroke="#5e5e5f"/><path d="M3 8h14" stroke="#5e5e5f"/><path d="M7 2v3M13 2v3" stroke="#5e5e5f" stroke-linecap="round"/></svg>
                </span>
              </div>
              <span id="tk-dob-hint-${idx}" class="test-kit-registration__sr">Use format DD/MM/YYYY</span>
              <p class="test-kit-registration__field-error" data-field-error="dob" data-person="${idx}"></p>
              <p class="test-kit-registration__age-error" data-dob-error="${idx}" role="alert"${dobBlockedInit ? "" : ' hidden=""'}>${escapeHtml(getAgeMessageUnder18())}</p>
            </div>
            <div class="test-kit-registration__field">
              <label class="test-kit-registration__sr" for="tk-gender-${idx}">Gender</label>
              <select id="tk-gender-${idx}" class="test-kit-registration__select" data-field="gender" data-person="${idx}" aria-label="Gender">
                <option value="" disabled${!person.gender ? " selected" : ""}>Gender</option>
                ${GENDERS.filter(Boolean)
                  .map(
                    (g) =>
                      `<option value="${escapeHtml(g)}"${
                        g === person.gender ? " selected" : ""
                      }>${escapeHtml(g)}</option>`,
                  )
                  .join("")}
              </select>
              <p class="test-kit-registration__field-error" data-field-error="gender" data-person="${idx}"></p>
            </div>
          </div>`,
        ),
      );

      const same = !!person.same_as_delivery_address;
      card.appendChild(
        buildSection(
          "Address",
          `
          <div class="test-kit-registration__stack">
            <div class="test-kit-registration__checkbox-row">
              <input type="checkbox" id="tk-same-${idx}" class="test-kit-registration__checkbox" data-field="same_as_delivery_address" data-person="${idx}" ${same ? "checked" : ""}>
              <label for="tk-same-${idx}" class="test-kit-registration__checkbox-label">Use the same address that will be entered at checkout</label>
            </div>
            <div class="test-kit-registration__address-fields" data-address-fields="${idx}" ${same ? 'hidden=""' : ""}>
              <div class="test-kit-registration__stack">
                <div class="test-kit-registration__field">
                  <label class="test-kit-registration__sr" for="tk-a1-${idx}">Address line 1</label>
                  <input id="tk-a1-${idx}" class="test-kit-registration__input" type="text" autocomplete="address-line1" placeholder="Address Line 1" data-field="address_1" data-person="${idx}" value="${escapeHtml(person.address_1 || "")}">
                  <p class="test-kit-registration__field-error" data-field-error="address_1" data-person="${idx}"></p>
                </div>
                <div class="test-kit-registration__field">
                  <label class="test-kit-registration__sr" for="tk-a2-${idx}">Address line 2</label>
                  <input id="tk-a2-${idx}" class="test-kit-registration__input" type="text" autocomplete="address-line2" placeholder="Address Line 2" data-field="address_2" data-person="${idx}" value="${escapeHtml(person.address_2 || "")}">
                </div>
                <div class="test-kit-registration__row">
                  <div class="test-kit-registration__field">
                    <label class="test-kit-registration__sr" for="tk-town-${idx}">Town</label>
                    <input id="tk-town-${idx}" class="test-kit-registration__input" type="text" placeholder="Town" data-field="town" data-person="${idx}" value="${escapeHtml(person.town || "")}">
                    <p class="test-kit-registration__field-error" data-field-error="town" data-person="${idx}"></p>
                  </div>
                  <div class="test-kit-registration__field">
                    <label class="test-kit-registration__sr" for="tk-state-${idx}">State</label>
                    <input id="tk-state-${idx}" class="test-kit-registration__input" type="text" placeholder="State" data-field="state" data-person="${idx}" value="${escapeHtml(person.state || "")}">
                    <p class="test-kit-registration__field-error" data-field-error="state" data-person="${idx}"></p>
                  </div>
                </div>
                <div class="test-kit-registration__field">
                  <label class="test-kit-registration__sr" for="tk-post-${idx}">Postcode</label>
                  <input id="tk-post-${idx}" class="test-kit-registration__input" type="text" autocomplete="postal-code" placeholder="Postcode" data-field="postcode" data-person="${idx}" value="${escapeHtml(person.postcode || "")}">
                  <p class="test-kit-registration__field-error" data-field-error="postcode" data-person="${idx}"></p>
                </div>
              </div>
            </div>
          </div>`,
        ),
      );

      host.appendChild(card);
    });

    host.querySelectorAll("[data-field]").forEach((el) => {
      const ev = el.type === "checkbox" ? "change" : "input";
      el.addEventListener(ev, onFieldInput);
      if (el.getAttribute("data-field") === "email") {
        el.addEventListener("blur", onEmailBlur);
      }
    });

    host.querySelectorAll("[data-remove-person]").forEach((btn) => {
      btn.addEventListener("click", () => {
        onFormRemoveClick(parseInt(btn.getAttribute("data-remove-person"), 10));
      });
    });

    host.querySelectorAll("[data-edit-person]").forEach((btn) => {
      btn.addEventListener("click", () => {
        onSummaryEditClick(parseInt(btn.getAttribute("data-edit-person"), 10));
      });
    });

    host.querySelectorAll("[data-delete-person]").forEach((btn) => {
      btn.addEventListener("click", () => {
        onSummaryDeleteClick(
          parseInt(btn.getAttribute("data-delete-person"), 10),
        );
      });
    });

    refreshAllAgeBlocks();
    updateAddPersonButtonState();
  }

  function buildSection(title, innerHtml) {
    const wrap = document.createElement("div");
    wrap.className = "test-kit-registration__stack";
    const lab = document.createElement("p");
    lab.className = "test-kit-registration__section-label";
    lab.textContent = title;
    wrap.appendChild(lab);
    const inner = document.createElement("div");
    inner.innerHTML = innerHtml;
    while (inner.firstChild) {
      wrap.appendChild(inner.firstChild);
    }
    return wrap;
  }

  /**
   * @param {string} value DOB stored as DD/MM/YYYY or ISO YYYY-MM-DD
   * @returns {string} DD/MM/YYYY for display
   */
  function formatDobForDisplay(value) {
    const v = (value || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      return `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}`;
    }
    return v;
  }

  /**
   * @param {object} person
   * @returns {string} title + first + last, space separated
   */
  function personSummaryName(person) {
    return [person.title, person.first_name, person.last_name]
      .map((s) => (s || "").trim())
      .filter(Boolean)
      .join(" ");
  }

  /**
   * Renders a read-only summary of a completed person plus Edit/Delete CTAs.
   *
   * @param {HTMLElement} card
   * @param {object} person
   * @param {number} idx
   */
  function appendPersonSummary(card, person, idx) {
    const name = personSummaryName(person);
    const email = (person.email || "").trim();
    const phone = (person.phone || "").trim();
    const dob = formatDobForDisplay(person.dob);
    const gender = (person.gender || "").trim();

    const rows = [
      `<p class="test-kit-registration__summary-row">` +
        `<span class="test-kit-registration__summary-label">Name:</span> ` +
        `${escapeHtml(name)}</p>`,
      `<p class="test-kit-registration__summary-row test-kit-registration__summary-row--strong">Your Details:</p>`,
    ];
    if (email) {
      rows.push(
        `<p class="test-kit-registration__summary-row">${escapeHtml(email)}</p>`,
      );
    }
    if (phone) {
      rows.push(
        `<p class="test-kit-registration__summary-row">${escapeHtml(phone)}</p>`,
      );
    }
    if (dob) {
      rows.push(
        `<p class="test-kit-registration__summary-row">${escapeHtml(dob)}</p>`,
      );
    }
    if (gender) {
      rows.push(
        `<p class="test-kit-registration__summary-row">${escapeHtml(gender)}</p>`,
      );
    }
    if (!person.same_as_delivery_address) {
      const line1 = [person.address_1, person.address_2]
        .map((s) => (s || "").trim())
        .filter(Boolean)
        .join(", ");
      const line2 = [person.town, person.state, person.postcode]
        .map((s) => (s || "").trim())
        .filter(Boolean)
        .join(", ");
      if (line1 || line2) {
        const l1 = line1
          ? `<p class="test-kit-registration__summary-line">${escapeHtml(
              line1,
            )}${line2 ? "," : ""}</p>`
          : "";
        const l2 = line2
          ? `<p class="test-kit-registration__summary-line">${escapeHtml(
              line2,
            )}</p>`
          : "";
        rows.push(
          `<div class="test-kit-registration__summary-row">${l1}${l2}</div>`,
        );
      }
    }

    const wrap = document.createElement("div");
    wrap.innerHTML = `
      <div class="test-kit-registration__summary" data-person-summary="${idx}">
        ${rows.join("")}
      </div>
      <div class="test-kit-registration__summary-actions">
        <button type="button" class="test-kit-registration__summary-link" data-edit-person="${idx}">Edit</button>
        <button type="button" class="test-kit-registration__summary-link" data-delete-person="${idx}">Delete</button>
      </div>`;
    while (wrap.firstChild) {
      card.appendChild(wrap.firstChild);
    }
  }

  /**
   * Flags persons that share an email with another person. Mutates the passed
   * error maps, but never overrides a more specific email error (required /
   * invalid format).
   *
   * @param {Record<string, string>[]} errorsByPerson
   */
  function applyDuplicateEmailErrors(errorsByPerson) {
    const counts = new Map();
    persons.forEach((p) => {
      const email = (p.email || "").trim().toLowerCase();
      if (email) {
        counts.set(email, (counts.get(email) || 0) + 1);
      }
    });
    persons.forEach((p, i) => {
      const email = (p.email || "").trim().toLowerCase();
      if (!email || (counts.get(email) || 0) <= 1) {
        return;
      }
      if (!errorsByPerson[i].email) {
        errorsByPerson[i].email = DUP_EMAIL_MSG;
      }
    });
  }

  /**
   * Per-field errors for every person, including cross-person duplicate emails.
   *
   * @returns {Record<string, string>[]}
   */
  function computeErrorsByPerson() {
    const errs = persons.map((p) => validatePersonFields(p));
    applyDuplicateEmailErrors(errs);
    return errs;
  }

  /**
   * Live duplicate-email feedback while typing. Shows/clears only the duplicate
   * message so it never clobbers required/invalid email errors.
   */
  function refreshEmailDuplicateErrors() {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    const counts = new Map();
    persons.forEach((p) => {
      const email = (p.email || "").trim().toLowerCase();
      if (email) {
        counts.set(email, (counts.get(email) || 0) + 1);
      }
    });
    persons.forEach((p, i) => {
      const errEl = dialog.querySelector(
        `[data-field-error="email"][data-person="${i}"]`,
      );
      const control = dialog.querySelector(
        `[data-field="email"][data-person="${i}"]`,
      );
      if (!errEl || !control) {
        return;
      }
      const email = (p.email || "").trim().toLowerCase();
      const isDuplicate = Boolean(email) && (counts.get(email) || 0) > 1;
      if (isDuplicate) {
        errEl.textContent = DUP_EMAIL_MSG;
        errEl.setAttribute("role", "alert");
        control.classList.add("test-kit-registration__control--error");
      } else if (errEl.textContent === DUP_EMAIL_MSG) {
        errEl.textContent = "";
        errEl.removeAttribute("role");
        control.classList.remove("test-kit-registration__control--error");
      }
    });
  }

  /**
   * True when no user-editable field has been touched (a freshly added person
   * that was never filled in).
   *
   * @param {object} person
   * @returns {boolean}
   */
  function isPersonEmpty(person) {
    const fields = [
      "title",
      "first_name",
      "last_name",
      "email",
      "phone",
      "gender",
      "dob",
      "address_1",
      "address_2",
      "town",
      "state",
      "postcode",
    ];
    return fields.every((f) => !(person[f] && String(person[f]).trim()));
  }

  /**
   * @param {Record<string, string>} fieldErrors
   * @param {object} person
   * @returns {boolean} true when no field errors and not age-blocked
   */
  function isPersonComplete(fieldErrors, person) {
    return Object.keys(fieldErrors).length === 0 && !isPersonAgeBlocked(person);
  }

  /**
   * Enables "Add Another Person" only when every person still in form mode is
   * fully valid (including unique email); disabled otherwise. No-op while
   * editing an existing cart line (the button is hidden in that mode).
   */
  function updateAddPersonButtonState() {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    const addBtn = dialog.querySelector("[data-test-kit-add-person]");
    if (!addBtn || editingLineKey) {
      return;
    }
    const errs = computeErrorsByPerson();
    const editing = persons
      .map((p, i) => ({ person: p, errors: errs[i] }))
      .filter((entry) => !entry.person.collapsed);
    // When every person is collapsed into a summary (e.g. after removing the
    // only in-progress person), there's nothing to validate, so adding another
    // is allowed. Otherwise every in-progress person must be valid.
    const allValid = editing.every((entry) =>
      isPersonComplete(entry.errors, entry.person),
    );
    addBtn.disabled = !allValid;
  }

  /**
   * Collapses the editing person(s) into a summary and adds a fresh form.
   */
  function onAddPersonClick() {
    syncFromDomBeforeReRender();
    const errorsByPerson = computeErrorsByPerson();
    const hasFieldErrors = errorsByPerson.some(
      (fe) => Object.keys(fe).length > 0,
    );
    if (hasFieldErrors) {
      showFieldErrors(errorsByPerson);
      updateAddPersonButtonState();
      return;
    }
    if (persons.some((p) => !p.collapsed && isPersonAgeBlocked(p))) {
      refreshAllAgeBlocks();
      return;
    }
    persons.forEach((p) => {
      if (!p.collapsed) {
        p.collapsed = true;
      }
    });
    persons.push(createEmptyPerson());
    renderPersonFields();
    updateEditModeUi();
  }

  /**
   * Re-opens a summarised person for editing.
   *
   * @param {number} idx
   */
  function onSummaryEditClick(idx) {
    syncFromDomBeforeReRender();

    const target = persons[idx];
    if (!target) {
      return;
    }

    // An accidentally-added person that was never filled in is discarded, so
    // editing reverts to the previously completed person rather than blocking.
    persons = persons.filter((p) => p.collapsed || !isPersonEmpty(p));

    // Any remaining person in form mode (partially filled) must be completed
    // before another person can be re-opened for editing.
    const errorsByPerson = computeErrorsByPerson();
    const hasFieldErrors = persons.some(
      (p, i) => !p.collapsed && Object.keys(errorsByPerson[i]).length > 0,
    );
    if (hasFieldErrors) {
      const firstInvalid = showFieldErrors(errorsByPerson);
      if (firstInvalid) {
        const focusInvalid = () => {
          firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof firstInvalid.focus === "function") {
            firstInvalid.focus({ preventScroll: true });
          }
        };
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(focusInvalid);
        } else {
          focusInvalid();
        }
      }
      return;
    }
    if (persons.some((p) => !p.collapsed && isPersonAgeBlocked(p))) {
      refreshAllAgeBlocks();
      const firstAgeErr = getDialog().querySelector(
        "[data-dob-error]:not([hidden])",
      );
      if (firstAgeErr) {
        firstAgeErr.setAttribute("tabindex", "-1");
        firstAgeErr.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof firstAgeErr.focus === "function") {
          firstAgeErr.focus({ preventScroll: true });
        }
      }
      return;
    }

    target.collapsed = false;
    renderPersonFields();
    updateEditModeUi();
  }

  /**
   * Removes a person still in form mode (header Remove). At least one person
   * must remain.
   *
   * @param {number} idx
   */
  function onFormRemoveClick(idx) {
    if (persons.length <= 1) {
      return;
    }
    syncFromDomBeforeReRender();
    persons.splice(idx, 1);
    // A lone remaining person is always shown as an editable form, never a
    // read-only summary.
    if (persons.length === 1) {
      persons[0].collapsed = false;
    }
    renderPersonFields();
    updateEditModeUi();
    updateAddPersonButtonState();
  }

  /**
   * Removes a summarised person; reindexing makes the next person the default.
   *
   * @param {number} idx
   */
  function onSummaryDeleteClick(idx) {
    syncFromDomBeforeReRender();
    persons.splice(idx, 1);
    if (persons.length === 0) {
      persons.push(createEmptyPerson());
    }
    renderPersonFields();
    updateEditModeUi();
  }

  function onFieldInput(e) {
    const el = e.target;
    const idx = parseInt(el.getAttribute("data-person"), 10);
    const field = el.getAttribute("data-field");
    if (Number.isNaN(idx) || !field || !persons[idx]) {
      return;
    }
    if (field === "same_as_delivery_address") {
      persons[idx].same_as_delivery_address = el.checked;
      const addr = getDialog().querySelector(`[data-address-fields="${idx}"]`);
      if (addr) {
        if (el.checked) {
          addr.setAttribute("hidden", "");
        } else {
          addr.removeAttribute("hidden");
        }
      }
      updateAddPersonButtonState();
      return;
    }
    if (field === "dob") {
      const isDelete =
        typeof e.inputType === "string" && e.inputType.indexOf("delete") === 0;
      const formatted = formatDobInput(el.value, isDelete);
      if (formatted !== el.value) {
        el.value = formatted;
      }
    }
    persons[idx][field] = el.value;
    clearFieldError(idx, field);
    if (field === "dob") {
      applyAgeBlockState(idx);
    }
    if (field === "email") {
      refreshEmailDuplicateErrors();
    }
    updateAddPersonButtonState();
  }

  /**
   * Validates email format when the field loses focus, so the user sees the
   * error after finishing typing rather than on every keystroke.
   *
   * @param {FocusEvent} e
   */
  function onEmailBlur(e) {
    const el = e.target;
    const idx = parseInt(el.getAttribute("data-person"), 10);
    if (Number.isNaN(idx) || !persons[idx]) {
      return;
    }
    const email = (persons[idx].email || "").trim();
    if (email && !validateEmail(email)) {
      setFieldError(idx, "email", EMAIL_INVALID_MSG);
      return;
    }
    refreshEmailDuplicateErrors();
  }

  /**
   * Toggles the inline under-18 error and disables the fields below DOB
   * while a person is age-blocked.
   *
   * @param {number} idx
   */
  function applyAgeBlockState(idx) {
    const dialog = getDialog();
    if (!dialog || !persons[idx]) {
      return;
    }
    const blocked = isPersonAgeBlocked(persons[idx]);
    const errEl = dialog.querySelector(`[data-dob-error="${idx}"]`);
    if (errEl) {
      if (blocked) {
        errEl.removeAttribute("hidden");
      } else {
        errEl.setAttribute("hidden", "");
      }
    }
    const dobInput = dialog.querySelector(
      `[data-field="dob"][data-person="${idx}"]`,
    );
    if (dobInput) {
      dobInput.classList.toggle(
        "test-kit-registration__control--error",
        blocked,
      );
    }
    const card = dialog.querySelector(`[data-person-index="${idx}"]`);
    if (!card) {
      return;
    }
    const belowFields = [
      "gender",
      "same_as_delivery_address",
      "address_1",
      "address_2",
      "town",
      "state",
      "postcode",
    ];
    belowFields.forEach((f) => {
      const node = card.querySelector(
        `[data-field="${f}"][data-person="${idx}"]`,
      );
      if (node) {
        node.disabled = blocked;
      }
    });
    card.classList.toggle(
      "test-kit-registration__person-card--age-blocked",
      blocked,
    );
  }

  function refreshAllAgeBlocks() {
    persons.forEach((_, i) => applyAgeBlockState(i));
  }

  function syncFromDomBeforeReRender() {
    const root = getDialog();
    if (!root) {
      return;
    }
    root.querySelectorAll("[data-field][data-person]").forEach((el) => {
      const idx = parseInt(el.getAttribute("data-person"), 10);
      const field = el.getAttribute("data-field");
      if (Number.isNaN(idx) || !persons[idx]) {
        return;
      }
      if (field === "same_as_delivery_address") {
        persons[idx].same_as_delivery_address = el.checked;
      } else if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLSelectElement
      ) {
        persons[idx][field] = el.value;
      }
    });
  }

  function setFormError(msg) {
    const dialog = getDialog();
    const el = dialog
      ? dialog.querySelector(".test-kit-registration__error")
      : null;
    if (!el) {
      return;
    }
    if (msg) {
      el.textContent = msg;
      el.removeAttribute("hidden");
      el.setAttribute("role", "alert");
      el.setAttribute("tabindex", "-1");
      const scrollAndFocus = () => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        if (typeof el.focus === "function") {
          el.focus({ preventScroll: true });
        }
      };
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollAndFocus);
        });
      } else {
        setTimeout(scrollAndFocus, 0);
      }
    } else {
      el.textContent = "";
      el.setAttribute("hidden", "");
      el.removeAttribute("tabindex");
      el.removeAttribute("role");
    }
  }

  function getFocusable(root) {
    const sel = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");
    return Array.from(root.querySelectorAll(sel)).filter(
      (n) => n.offsetParent !== null || n === document.activeElement,
    );
  }

  function onTrapKeydown(e) {
    if (e.key !== "Tab") {
      return;
    }
    const dialog = getDialog();
    if (!dialog || !dialog.open) {
      return;
    }
    const list = getFocusable(dialog);
    if (list.length === 0) {
      return;
    }
    const first = list[0];
    const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openModal(mode) {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    if (mode !== "edit") {
      syncVariantFromForm();
      editingLineKey = null;
      persons = [createEmptyPerson()];
    }
    setFormError("");
    const ack = dialog.querySelector("[data-test-kit-ack]");
    if (ack) {
      ack.checked = false;
    }
    renderPersonFields();
    updateEditModeUi();
    document.removeEventListener("keydown", onTrapKeydown);
    lastFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
    const closeBtn = dialog.querySelector("[data-test-kit-close]");
    if (closeBtn) {
      closeBtn.focus();
    }
    document.addEventListener("keydown", onTrapKeydown);
  }

  function closeModal() {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    document.removeEventListener("keydown", onTrapKeydown);
    if (typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
    }
    editingLineKey = null;
  }

  function collectPersonsFromForm() {
    syncFromDomBeforeReRender();
    return persons.map((p) => buildPersonPayload(p));
  }

  /**
   * Looks up a cart line by its key.
   *
   * @param {string} key cart line key
   * @returns {Promise<{ item: object; line: number } | null>} null if absent
   */
  async function getCartLineByKey(key) {
    const cartRes = await fetch("/cart.js");
    if (!cartRes.ok) {
      throw new Error("cart");
    }
    const cart = await cartRes.json();
    const item = cart.items.find((it) => it.key === key);
    if (!item) {
      return null;
    }
    const idx = cart.items.indexOf(item);
    return { item, line: item.line || idx + 1 };
  }

  /**
   * Removes a cart line by key.
   *
   * @param {string} key cart line key
   * @returns {Promise<boolean>} false when the line was not found in the cart
   */
  async function removeLineByKey(key) {
    const found = await getCartLineByKey(key);
    if (!found) {
      return false;
    }
    const ch = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line: found.line, quantity: 0 }),
    });
    if (!ch.ok) {
      throw new Error("remove");
    }
    return true;
  }

  /**
   * Best-effort re-add of a removed line from its original cart properties, used
   * to restore the cart when an edit fails partway through. Swallows errors.
   *
   * @param {number|string} variantId
   * @param {Record<string, string>} properties original line item properties
   */
  async function restoreOriginalLine(variantId, properties) {
    try {
      const fd = new FormData();
      fd.append("id", String(Number(variantId)));
      fd.append("quantity", "1");
      Object.keys(properties || {}).forEach((k) => {
        const v = properties[k];
        if (v === undefined || v === null) {
          return;
        }
        fd.append(`properties[${k}]`, String(v));
      });
      await fetch("/cart/add.js", { method: "POST", body: fd });
    } catch (e) {
      // Nothing else we can do; leave any cleanup to the user/cart.
    }
  }

  /**
   * Best-effort removal of lines added during a failed submit so the cart is
   * left in its original state. Failures here are swallowed; the caller still
   * surfaces the original error.
   *
   * @param {string[]} keys cart line keys added so far
   */
  async function rollbackAddedLines(keys) {
    for (let i = 0; i < keys.length; i++) {
      try {
        await removeLineByKey(keys[i]);
      } catch (e) {
        // Nothing else we can do; leave remaining cleanup to the user/cart.
      }
    }
  }

  async function submitRegistration() {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    const ack = dialog.querySelector("[data-test-kit-ack]");
    if (!ack || !ack.checked) {
      setFormError(
        "Please confirm the details match the person using the test kit.",
      );
      return;
    }
    setFormError("");
    const payloads = collectPersonsFromForm();
    refreshAllAgeBlocks();

    // Field-level validation: each invalid field shows its error inline below
    // the field with a #F04E51 border (no longer in the top banner).
    const errorsByPerson = computeErrorsByPerson();
    const hasFieldErrors = errorsByPerson.some(
      (fe) => Object.keys(fe).length > 0,
    );
    if (hasFieldErrors) {
      const firstInvalid = showFieldErrors(errorsByPerson);
      if (firstInvalid) {
        const focusInvalid = () => {
          firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof firstInvalid.focus === "function") {
            firstInvalid.focus({ preventScroll: true });
          }
        };
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(focusInvalid);
        } else {
          focusInvalid();
        }
      }
      return;
    }
    // Under-18 is surfaced inline (.test-kit-registration__age-error), not in
    // the top banner. Still block add-to-cart and scroll to the first notice.
    if (persons.some(isPersonAgeBlocked)) {
      const firstAgeErr = dialog.querySelector(
        "[data-dob-error]:not([hidden])",
      );
      if (firstAgeErr) {
        firstAgeErr.setAttribute("tabindex", "-1");
        const focusAgeErr = () => {
          firstAgeErr.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof firstAgeErr.focus === "function") {
            firstAgeErr.focus({ preventScroll: true });
          }
        };
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(focusAgeErr);
        } else {
          focusAgeErr();
        }
      }
      return;
    }

    const variantId = getVariantId();
    if (!variantId) {
      setFormError("No variant selected. Please refresh and try again.");
      return;
    }

    const submitBtn = dialog.querySelector("[data-test-kit-submit]");
    if (submitBtn) {
      submitBtn.disabled = true;
    }

    try {
      // When editing, remove the original line first. Removing before re-adding
      // prevents an unchanged edit (identical properties => identical line key)
      // from merging into the line we're about to remove and disappearing. The
      // original properties/variant are captured so the line can be restored if
      // a later add fails, and a missing line is treated as a stale edit instead
      // of silently adding a duplicate.
      let restoreProps = null;
      let restoreVariantId = null;
      if (editingLineKey) {
        const original = await getCartLineByKey(editingLineKey);
        if (!original) {
          throw new Error("stale-edit");
        }
        restoreProps = original.item.properties || {};
        restoreVariantId = original.item.variant_id || original.item.id;
        await removeLineByKey(editingLineKey);
      }

      // Track newly added line keys so a mid-batch failure can be rolled back,
      // leaving the cart in its original state.
      const addedKeys = [];
      for (let i = 0; i < payloads.length; i++) {
        const props = buildFlatPropertiesForOnePerson(payloads[i]);
        const fd = new FormData();
        fd.append("id", String(Number(variantId)));
        fd.append("quantity", "1");
        Object.keys(props).forEach((k) => {
          const v = props[k];
          if (v === undefined || v === null) {
            return;
          }
          fd.append(`properties[${k}]`, String(v));
        });
        const addRes = await fetch("/cart/add.js", {
          method: "POST",
          body: fd,
        });
        if (!addRes.ok) {
          const errText = await addRes.text();
          await rollbackAddedLines(addedKeys);
          if (restoreProps) {
            await restoreOriginalLine(restoreVariantId, restoreProps);
          }
          throw new Error(errText || "add failed");
        }
        try {
          const added = await addRes.json();
          if (added && added.key) {
            addedKeys.push(added.key);
          }
        } catch (e) {
          // Response body isn't required for success; ignore parse issues.
        }
      }

      closeModal();
      document.dispatchEvent(new CustomEvent("cart:updated"));

      const cartDrawer = document.querySelector("cart-drawer");
      const cartType = document.body.dataset.cartType;
      if (cartDrawer && cartType === "drawer") {
        await cartDrawer.getCart();
        await cartDrawer.updateCartUI();
        cartDrawer.open();
      } else if (cartType === "page") {
        window.location.href = "/cart";
      } else if (cartType === "rebuy") {
        // Rebuy opens the smart cart on /cart/add.js; avoid redirecting to
        // /cart (which re-opened the flyout and felt broken).
      } else {
        window.location.href = "/cart";
      }
    } catch (err) {
      console.error(err);
      if (err && err.message === "stale-edit") {
        setFormError(
          "This item is no longer in your cart. Please close and reopen it to make changes.",
        );
      } else {
        setFormError("Something went wrong. Please try again.");
      }
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
      }
    }
  }

  function onDocumentSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) {
      return;
    }
    if (!form.matches(SEL_FORM)) {
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    openModal("add");
  }

  /**
   * Applies a variant id to the registration config and opens
   * `.test-kit-registration`.
   *
   * @param {number|null} variantId
   */
  function openRegistration(variantId) {
    if (!getDialog()) {
      return;
    }

    if (variantId) {
      cfg.defaultVariantId = variantId;
    }

    openModal("add");
  }

  /**
   * Opens registration for a Rebuy add that `test-kit-rebuy-guard.js` handled.
   * `unregisteredVariantId` is set when the kit already reached the cart, so
   * that line is dropped before the shopper registers it.
   *
   * @param {CustomEvent} event
   */
  async function onRegisterRequest(event) {
    const detail = event && event.detail ? event.detail : {};
    if (!getDialog()) {
      return;
    }

    if (detail.config) {
      setActiveProduct(detail.config);
    }

    if (detail.unregisteredVariantId) {
      await removeUnregisteredLine(detail.unregisteredVariantId);
    }

    const variantId = Number(detail.variantId);
    openRegistration(
      Number.isFinite(variantId) && variantId > 0 ? variantId : null,
    );
  }

  /**
   * Drops a test kit line that reached the cart with no registration details,
   * so the shopper does not end up with a duplicate once they register.
   *
   * @param {number} variantId
   * @returns {Promise<void>}
   */
  async function removeUnregisteredLine(variantId) {
    try {
      const res = await fetch("/cart.js");
      if (!res.ok) {
        return;
      }

      const cart = await res.json();
      const items = Array.isArray(cart.items) ? cart.items : [];
      const bare = [...items].reverse().find((item) => {
        const vid = item.variant_id || item.id;
        if (Number(vid) !== Number(variantId)) {
          return false;
        }
        const props = item.properties || {};
        return (
          props._test_kit_email == null &&
          props._test_kit_line_uid == null &&
          props._test_kit_persons == null
        );
      });

      if (bare && bare.key) {
        await removeLineByKey(bare.key);
        document.dispatchEvent(new CustomEvent("cart:updated"));
      }
    } catch (e) {
      console.error(e);
    }
  }

  function bindDialogUi() {
    const dialog = getDialog();
    if (!dialog) {
      return;
    }
    dialog.addEventListener("close", () => {
      document.removeEventListener("keydown", onTrapKeydown);
      setActiveProduct(pageProduct);
      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
      }
      lastFocused = null;
    });
    dialog
      .querySelector("[data-test-kit-close]")
      ?.addEventListener("click", () => {
        closeModal();
      });
    dialog
      .querySelector("[data-test-kit-submit]")
      ?.addEventListener("click", () => {
        submitRegistration();
      });
    dialog
      .querySelector("[data-test-kit-add-person]")
      ?.addEventListener("click", onAddPersonClick);

    if (tryOpenRegisterFromUrl()) {
      return;
    }

    void tryOpenEditFromUrl();
  }

  /**
   * Opens registration for a test kit added from a Rebuy widget on another
   * page, which the click guard redirects here.
   *
   * @returns {boolean} true when the form was opened
   */
  function tryOpenRegisterFromUrl() {
    const params = new URLSearchParams(window.location.search);
    if (params.get("test_kit_register") !== "1") {
      return false;
    }

    openRegistration(null);

    const clean = new URL(window.location.href);
    clean.searchParams.delete("test_kit_register");
    window.history.replaceState({}, "", clean.toString());
    return true;
  }

  async function tryOpenEditFromUrl() {
    const params = new URLSearchParams(window.location.search);
    if (params.get("test_kit_edit") !== "1") {
      return;
    }
    const key = params.get("key");
    if (!key) {
      return;
    }
    await openEditFromCartLineKey(key);
    const clean = new URL(window.location.href);
    clean.searchParams.delete("test_kit_edit");
    clean.searchParams.delete("key");
    window.history.replaceState({}, "", clean.toString());
  }

  async function openEditFromCartLineKey(key) {
    try {
      const res = await fetch("/cart.js");
      if (!res.ok) {
        return;
      }
      const cart = await res.json();
      const item = cart.items.find((it) => it.key === key);
      if (!item) {
        return;
      }
      const props = item.properties || {};
      const parsed = extractPersonsFromLineItemProperties(props);
      if (!parsed.length) {
        return;
      }
      editingLineKey = key;
      persons = parsed.map((p) => ({
        title: p.title || "",
        first_name: p.first_name || "",
        last_name: p.last_name || "",
        email: p.email || "",
        phone: p.phone || "",
        gender: p.gender || "",
        dob: p.dob || "",
        same_as_delivery_address: p.same_as_delivery_address !== false,
        address_1: p.address_1 || "",
        address_2: p.address_2 || "",
        town: p.town || "",
        state: p.state || "",
        postcode: p.postcode || "",
      }));
      cfg.defaultVariantId = item.variant_id;
      openModal("edit");
    } catch (err) {
      console.error(err);
    }
  }

  async function onEditClick(e) {
    const btn = e.target.closest("[data-test-kit-edit]");
    if (!btn) {
      return;
    }
    e.preventDefault();
    const key = btn.getAttribute("data-item-key");
    if (!key) {
      return;
    }
    await openEditFromCartLineKey(key);
  }

  document.addEventListener("submit", onDocumentSubmit, true);
  document.addEventListener("click", onEditClick);
  document.addEventListener("test-kit:register", onRegisterRequest);

  document.addEventListener("variant:changed", (e) => {
    if (!e.detail || String(e.detail.productId) !== String(cfg.productId)) {
      return;
    }
    const v = e.detail.variant;
    if (v && v.id) {
      cfg.defaultVariantId = v.id;
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bindDialogUi);
  } else {
    bindDialogUi();
  }
})();
